import React, { useState, useCallback } from 'react';
import {
    Button,
    Card,
    Group,
    Text,
    TextInput,
    Textarea,
    ActionIcon,
    Stack,
    Alert,
    Collapse,
    Select,
    Badge,
    Checkbox,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import {
    IconTrash,
    IconPlus,
    IconHelpCircle,
    IconGripVertical,
    IconStar,
    IconStarFilled,
    IconLock,
    IconLockOpen,
} from '@tabler/icons-react';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    type DragEndEvent,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
    useSortable,
} from '@dnd-kit/sortable';
import {
    CSS,
} from '@dnd-kit/utilities';
import { t } from 'i18next';
import { modals } from '@mantine/modals';
import { IJournalQuestion } from '../../../global/entities/Journal/Journal.ts';
import {
    useJournalQuestionTypes,
    useAddQuestion,
    useUpdateQuestion,
    useDeleteQuestion,
    useReorderQuestions
} from '../../../global/entities/Journal/queries/useJournals.ts';
import { QUESTION_TYPE_IDS } from '../../PatientDashboard/PatientJournal/QuestionTypes.ts';
import { OptionsJsonHelper } from './OptionsJsonHelper.tsx';

interface IJournalQuestionManagerProps {
    questions: IJournalQuestion[];
    onQuestionsChange: (questions: IJournalQuestion[]) => void;
    categoryId?: number;
}

interface SortableQuestionItemProps {
    question: IJournalQuestion;
    index: number;
    isExpanded: boolean;
    questionTypeName: string;
    onToggleExpansion: () => void;
    onDelete: () => void;
    onUpdate: (updatedQuestion: Partial<IJournalQuestion>) => void;
    isDeleting: boolean;
    questionTypes: any[];
}

const SortableQuestionItem: React.FC<SortableQuestionItemProps> = ({
    question,
    index,
    isExpanded,
    questionTypeName,
    onToggleExpansion,
    onDelete,
    onUpdate,
    isDeleting,
    questionTypes,
}) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: question.id || `temp-${index}` });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    const questionTypeOptions = questionTypes?.map(type => ({
        value: type.id.toString(),
        label: type.name || `Type ${type.id}`,
    })) || [];

    return (
        <Card
            ref={setNodeRef}
            key={question.id || `new-${index}`}
            withBorder
            p="sm"
            style={{
                ...style,
                backgroundColor: 'var(--mantine-color-blue-0)',
                borderColor: 'var(--mantine-color-blue-2)',
                borderWidth: '1px',
                marginLeft: '20px',
                position: 'relative',
                cursor: isExpanded ? 'default' : 'pointer',
            }}
            onClick={!isExpanded ? onToggleExpansion : undefined}
        >
            <Group 
                justify="space-between" 
                mb="xs"
                style={{
                    cursor: isExpanded ? 'pointer' : 'pointer',
                }}
                onClick={isExpanded ? onToggleExpansion : undefined}
            >
                <Group gap="xs" style={{ cursor: isExpanded ? 'pointer' : 'pointer', flex: 1 }}>
                    <div
                        {...attributes}
                        {...listeners}
                        style={{
                            cursor: 'grab',
                            display: 'flex',
                            alignItems: 'center',
                            padding: '4px',
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <IconGripVertical size={16} color="var(--mantine-color-gray-6)" />
                    </div>
                    
                    <Text 
                        size="sm" 
                        fw={500} 
                        style={{ 
                            flex: 1,
                            cursor: 'pointer',
                        }}
                    >
                        {question.questionTextFr}
                    </Text>
                    
                    <Badge
                        size="xs"
                        variant="light"
                        style={{ cursor: 'pointer' }}
                    >
                        {questionTypeName}
                    </Badge>
                </Group>

                <Group gap={4}>
                    <ActionIcon
                        color={question.isPriority ? 'yellow' : 'gray'}
                        variant="subtle"
                        size="sm"
                        title={question.isPriority ? t('journal.removeFromPriority') : t('journal.markAsPriority')}
                        onClick={(e) => {
                            e.stopPropagation();
                            const newIsPriority = !question.isPriority;
                            onUpdate({
                                isPriority: newIsPriority,
                                isPriorityLocked: newIsPriority ? question.isPriorityLocked : false,
                            });
                        }}
                    >
                        {question.isPriority ? <IconStarFilled size={14} /> : <IconStar size={14} />}
                    </ActionIcon>

                    {question.isPriority && (
                        <ActionIcon
                            color={question.isPriorityLocked ? 'blue' : 'gray'}
                            variant="subtle"
                            size="sm"
                            title={question.isPriorityLocked ? t('journal.unlockPriority') : t('journal.lockPriority')}
                            onClick={(e) => {
                                e.stopPropagation();
                                onUpdate({ isPriorityLocked: !question.isPriorityLocked });
                            }}
                        >
                            {question.isPriorityLocked ? <IconLock size={14} /> : <IconLockOpen size={14} />}
                        </ActionIcon>
                    )}

                    <ActionIcon
                        color="red"
                        variant="subtle"
                        size="sm"
                        onClick={(e) => {
                            e.stopPropagation();
                            onDelete();
                        }}
                        loading={isDeleting}
                    >
                        <IconTrash size={12} />
                    </ActionIcon>
                </Group>
            </Group>

            <Collapse in={isExpanded}>
                <Stack gap="xs">
                    <Group grow>
                        <TextInput
                            label={t('journal.questionTextFr')}
                            value={question.questionTextFr}
                            onChange={(e) => onUpdate({ questionTextFr: e.target.value })}
                            size="sm"
                            name={`question_${index}_questionTextFr`}
                        />
                        
                        <TextInput
                            label={t('journal.questionTextEn')}
                            value={question.questionTextEn}
                            onChange={(e) => onUpdate({ questionTextEn: e.target.value })}
                            size="sm"
                            placeholder={t('journal.enterQuestionTextEn')}
                            name={`question_${index}_questionTextEn`}
                        />
                    </Group>

                    <Group grow>
                        <Textarea
                            label={t('journal.descriptionFr')}
                            value={question.descriptionFr || ''}
                            onChange={(e) => onUpdate({ descriptionFr: e.target.value })}
                            size="sm"
                            placeholder={t('journal.enterQuestionDescriptionFr')}
                            name={`question_${index}_descriptionFr`}
                            rows={2}
                        />
                        
                        <Textarea
                            label={t('journal.descriptionEn')}
                            value={question.descriptionEn || ''}
                            onChange={(e) => onUpdate({ descriptionEn: e.target.value })}
                            size="sm"
                            placeholder={t('journal.enterQuestionDescriptionEn')}
                            name={`question_${index}_descriptionEn`}
                            rows={2}
                        />
                    </Group>
                    
                    <Select
                        label={t('journal.questionType')}
                        value={question.questionTypeID?.toString()}
                        data={questionTypeOptions}
                        onChange={(value) => onUpdate({ questionTypeID: parseInt(value || '0') })}
                        size="sm"
                        name={`question_${index}_questionTypeID`}
                    />

                    {(question.questionTypeID === QUESTION_TYPE_IDS.YES_NO_WITH_SCALE || 
                      question.questionTypeID === QUESTION_TYPE_IDS.SCALE) && (
                        <Checkbox
                            label={t('journal.reversedScale')}
                            checked={question.isReversed || false}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => onUpdate({ isReversed: event.currentTarget.checked })}
                            size="sm"
                        />
                    )}

                    <Textarea
                        label={
                            <Group gap={4}>
                                {t('journal.optionsJson')}
                                <OptionsJsonHelper questionTypeID={question.questionTypeID} />
                            </Group>
                        }
                        value={question.optionsJson}
                        onChange={(e) => onUpdate({ optionsJson: e.target.value })}
                        rows={3}
                        size="sm"
                        name={`question_${index}_optionsJson`}
                    />
                </Stack>
            </Collapse>
        </Card>
    );
};

export const JournalQuestionManager: React.FC<IJournalQuestionManagerProps> = ({
    questions,
    onQuestionsChange,
    categoryId,
}) => {
    const [expandedQuestions, setExpandedQuestions] = useState<Set<number>>(new Set());
    const [showAddForm, setShowAddForm] = useState(false);
    
    const { data: questionTypes } = useJournalQuestionTypes();
    
    // Drag and drop sensors
    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    // Handle drag end
    const handleDragEnd = useCallback((event: DragEndEvent) => {
        const { active, over } = event;

        if (active.id !== over?.id) {
            const oldIndex = questions.findIndex((question) => 
                (question.id || `temp-${questions.indexOf(question)}`) === active.id
            );
            const newIndex = questions.findIndex((question) => 
                (question.id || `temp-${questions.indexOf(question)}`) === over?.id
            );

            if (oldIndex !== -1 && newIndex !== -1) {
                const newQuestions = arrayMove(questions, oldIndex, newIndex).map((question, index) => ({
                    ...question,
                    order: index,
                }));
                onQuestionsChange(newQuestions);
                
                // Sync with backend if needed
                if (categoryId && newQuestions.every(q => q.id)) {
                    // Call reorder mutation if all questions have IDs
                    reorderQuestionsMutation.mutate({
                        categoryId,
                        questionIds: newQuestions.map(q => q.id!)
                    });
                }
            }
        }
    }, [questions, onQuestionsChange]);
    
    // Backend mutations
    const addQuestionMutation = useAddQuestion();
    const updateQuestionMutation = useUpdateQuestion();
    const deleteQuestionMutation = useDeleteQuestion();
    const reorderQuestionsMutation = useReorderQuestions();

    const newQuestionForm = useForm({
        initialValues: {
            questionTextFr: '',
            questionTextEn: '',
            descriptionFr: '',
            descriptionEn: '',
            questionTypeID: '',
            optionsJson: '',
        },
        validate: {
            questionTextFr: (value) => (!value.trim() ? t('formError.requiredQuestionFr') : null),
            questionTextEn: (value) => (!value.trim() ? t('formError.requiredQuestionEn') : null),
            questionTypeID: (value) => (!value ? t('formError.questionTypeIsRequired') : null),
        },
    });

    const handleAddQuestion = (values: { questionTextFr: string; questionTextEn: string; descriptionFr: string; descriptionEn: string; questionTypeID: string; optionsJson: string }) => {
        try {
            if (!categoryId || categoryId <= 0) {
                // For new categories, add to local state
                const newQuestion: IJournalQuestion = {
                    id: 0, // New question
                    categoryID: categoryId || 0,
                    questionTextFr: values.questionTextFr,
                    questionTextEn: values.questionTextEn,
                    descriptionEn: values.descriptionEn || undefined,
                    descriptionFr: values.descriptionFr || undefined,
                    questionTypeID: parseInt(values.questionTypeID),
                    optionsJson: values.optionsJson,
                    order: questions.length,
                    isReversed: false,
                    isPriority: false,
                    isPriorityLocked: false,
                };

                onQuestionsChange([...questions, newQuestion]);
                newQuestionForm.reset();
                setShowAddForm(false);
                return;
            }

            // For existing categories, save to backend
            const questionData = {
                categoryID: categoryId,
                questionTextFr: values.questionTextFr,
                questionTextEn: values.questionTextEn,
                descriptionEn: values.descriptionEn || undefined,
                descriptionFr: values.descriptionFr || undefined,
                questionTypeID: parseInt(values.questionTypeID),
                optionsJson: values.optionsJson,
                order: questions.length,
                isReversed: false,
            };

            addQuestionMutation.mutate({
                categoryId: categoryId,
                question: questionData
            }, {
                onSuccess: (questionId) => {
                    // Add to local state with the returned ID
                    const newQuestion: IJournalQuestion = {
                        id: questionId,
                        categoryID: categoryId,
                        questionTextFr: values.questionTextFr,
                        questionTextEn: values.questionTextEn,
                        descriptionEn: values.descriptionEn || undefined,
                        descriptionFr: values.descriptionFr || undefined,
                        questionTypeID: parseInt(values.questionTypeID),
                        optionsJson: values.optionsJson,
                        order: questions.length,
                        isReversed: false,
                        isPriority: false,
                        isPriorityLocked: false,
                    };

                    onQuestionsChange([...questions, newQuestion]);
                    newQuestionForm.reset();
                    setShowAddForm(false);
                },
                onError: (error) => {
                    console.error('Failed to add question:', error);
                }
            });
        } catch (error) {
            console.error('Error in handleAddQuestion:', error);
        }
    };

    const openDeleteQuestionModal = (index: number) => {
        const questionToDelete = questions[index];
        const questionText = questionToDelete.questionTextFr || questionToDelete.questionTextEn || t('journal.thisQuestion');
        
        modals.openConfirmModal({
            title: t('modal.deleteQuestionModal.title'),
            children: (
                <Stack gap="sm">
                    <Text>{t('modal.deleteQuestionModal.question')}</Text>
                    <Text fw="bold" size="sm" c="dimmed">
                        "{questionText}"
                    </Text>
                    <Text fw="bold">{t('modal.deleteQuestionModal.details')}</Text>
                </Stack>
            ),
            labels: { 
                confirm: t('button.delete'), 
                cancel: t('button.cancel') 
            },
            confirmProps: { color: 'red' },
            cancelProps: { variant: 'outline', color: 'gray' },
            onConfirm: () => handleDeleteQuestion(index),
            size: 'lg',
            radius: 'xs',
        });
    };

    const handleDeleteQuestion = async (index: number) => {
        const questionToDelete = questions[index];
        
        if (!categoryId || !questionToDelete.id || questionToDelete.id <= 0) {
            // For new categories or new questions, just remove from local state
            const newQuestions = questions.filter((_, i) => i !== index)
                .map((question, i) => ({ ...question, order: i }));
            onQuestionsChange(newQuestions);
            return;
        }

        try {
            // Delete from backend
            await deleteQuestionMutation.mutateAsync(questionToDelete.id);
            
            // Remove from local state
            const newQuestions = questions.filter((_, i) => i !== index)
                .map((question, i) => ({ ...question, order: i }));
            onQuestionsChange(newQuestions);
        } catch (error) {
            console.error('Failed to delete question:', error);
            // Could add error handling UI here
        }
    };

    const handleUpdateQuestion = async (index: number, updatedQuestion: Partial<IJournalQuestion>) => {
        const question = questions[index];
        
        // Update local state immediately for better UX
        const newQuestions = [...questions];
        newQuestions[index] = { ...newQuestions[index], ...updatedQuestion };
        onQuestionsChange(newQuestions);

        // If it's an existing question with an ID, update in backend
        if (categoryId && question.id && question.id > 0) {
            try {
                await updateQuestionMutation.mutateAsync({
                    questionId: question.id,
                    question: {
                        id: question.id,
                        categoryID: categoryId,
                        questionTextFr: newQuestions[index].questionTextFr,
                        questionTextEn: newQuestions[index].questionTextEn,
                        descriptionEn: newQuestions[index].descriptionEn,
                        descriptionFr: newQuestions[index].descriptionFr,
                        questionTypeID: newQuestions[index].questionTypeID,
                        optionsJson: newQuestions[index].optionsJson,
                        order: newQuestions[index].order,
                        isReversed: newQuestions[index].isReversed,
                        isPriority: newQuestions[index].isPriority,
                        isPriorityLocked: newQuestions[index].isPriorityLocked,
                    }
                });
            } catch (error) {
                console.error('Failed to update question:', error);
                // Revert local changes on error
                onQuestionsChange(questions);
            }
        }
    };

    const toggleQuestionExpansion = (questionId: number) => {
        const newExpanded = new Set(expandedQuestions);
        if (newExpanded.has(questionId)) 
            newExpanded.delete(questionId);
         else 
            newExpanded.add(questionId);
        
        setExpandedQuestions(newExpanded);
    };

    // Transform question types for Select component\
    const questionTypeOptions = questionTypes?.map(type => ({
        value: type.id.toString(),
        label: type.name || `Type ${type.id}`,
    })) || [];

    return (
        <Stack gap="sm">
            <Group justify="space-between">
                    <Text size="lg" fw={600}>
                        {t('journal.questions')}
                    </Text>
                <Button
                    leftSection={<IconPlus size={14} />}
                    variant="outline"
                    size="xs"
                    onClick={() => setShowAddForm(!showAddForm)}
                    loading={addQuestionMutation.isPending}
                >
                    {t('journal.addQuestion')}
                </Button>
            </Group>

            <Collapse in={showAddForm}>
                <Card 
                    withBorder 
                    p="sm"
                    style={{
                        backgroundColor: 'var(--mantine-color-blue-0)',
                        borderColor: 'var(--mantine-color-blue-3)',
                        borderWidth: '2px',
                        borderStyle: 'dashed',
                        marginLeft: '20px',
                    }}
                >
                        <Stack gap="xs">
                            <Group grow>
                                <TextInput
                                    label={t('journal.questionTextFr')}
                                    placeholder={t('journal.enterQuestionTextFr')}
                                    {...newQuestionForm.getInputProps('questionTextFr')}
                                />
                                
                                <TextInput
                                    label={t('journal.questionTextEn')}
                                    placeholder={t('journal.enterQuestionTextEn')}
                                    {...newQuestionForm.getInputProps('questionTextEn')}
                                />
                            </Group>

                            <Group grow>
                                <Textarea
                                    label={t('journal.descriptionFr')}
                                    placeholder={t('journal.enterQuestionDescriptionFr')}
                                    rows={2}
                                    {...newQuestionForm.getInputProps('descriptionFr')}
                                />
                                
                                <Textarea
                                    label={t('journal.descriptionEn')}
                                    placeholder={t('journal.enterQuestionDescriptionEn')}
                                    rows={2}
                                    {...newQuestionForm.getInputProps('descriptionEn')}
                                />
                            </Group>
                            
                            <Select
                                label={t('journal.questionType')}
                                placeholder={t('journal.selectQuestionType')}
                                data={questionTypeOptions}
                                {...newQuestionForm.getInputProps('questionTypeID')}
                            />

                            <Textarea
                                label={
                                    <Group gap={4}>
                                        {t('journal.optionsJson')}
                                        <OptionsJsonHelper questionTypeID={parseInt(newQuestionForm.values.questionTypeID || '0')} />
                                    </Group>
                                }
                                placeholder={t('journal.enterOptionsJson')}
                                description={t('journal.optionsJsonDescription')}
                                rows={3}
                                {...newQuestionForm.getInputProps('optionsJson')}
                            />

                            <Group justify="flex-end">
                                <Button variant="outline" size="xs" onClick={() => setShowAddForm(false)}>
                                    {t('button.cancel')}
                                </Button>
                                <Button 
                                    size="xs"
                                    onClick={() => {
                                        // Manual validation
                                        const validation = newQuestionForm.validate();
                                        if (validation.hasErrors) 
                                            return;
                                        
                                        // Call handler with form values
                                        handleAddQuestion(newQuestionForm.values);
                                    }}
                                    loading={addQuestionMutation.isPending}
                                >
                                    {t('button.add')}
                                </Button>
                            </Group>
                        </Stack>
                </Card>
            </Collapse>

            {questions.length === 0 && (
                <Alert color="gray" icon={<IconHelpCircle />}>
                    {t('journal.noQuestionsYet')}
                </Alert>
            )}

            <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
            >
                <SortableContext
                    items={questions.map((question, index) => question.id || `temp-${index}`)}
                    strategy={verticalListSortingStrategy}
                >
                    {questions.map((question, index) => {
                        const isExpanded = expandedQuestions.has(question.id || index);
                        const questionTypeName = questionTypes?.find(type => type.id === question.questionTypeID)?.name || 'Unknown';
                        
                        return (
                            <SortableQuestionItem
                                key={question.id || `new-${index}`}
                                question={question}
                                index={index}
                                isExpanded={isExpanded}
                                questionTypeName={questionTypeName}
                                onToggleExpansion={() => toggleQuestionExpansion(question.id || index)}
                                onDelete={() => openDeleteQuestionModal(index)}
                                onUpdate={(updatedQuestion) => handleUpdateQuestion(index, updatedQuestion)}
                                isDeleting={deleteQuestionMutation.isPending}
                                questionTypes={questionTypes || []}
                            />
                        );
                    })}
                </SortableContext>
            </DndContext>
        </Stack>
    );
}; 